import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getUserByDocId, updateUser as updateUserThunk, getStudentByIdentifier, validateStudentsList } from './usersThunks'
import { User, Student } from '@/types/user.types'


interface UsersState {
  user: User | null
  student: User | null
  validatedStudents: Student[] | null
  isLoading: boolean
  error: string | null
}

const initialState: UsersState = {
  user: null,
  student: null,
  validatedStudents: null,
  isLoading: false,
  error: null,
}

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoading = false
    },
    clearError: (state) => {
      state.error = null
    },
    clearValidatedStudents: (state) => {
      state.validatedStudents = null
    },
    clearStudent: (state) => {
      state.student = null
    },
  },
  extraReducers: (builder) => {
    // Get User By Doc ID
    builder
      .addCase(getUserByDocId.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getUserByDocId.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload as User
        state.error = null
      })
      .addCase(getUserByDocId.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Update User
    builder
      .addCase(updateUserThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateUserThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload as User
        state.error = null
      })
      .addCase(updateUserThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
    // Get Student By Identifier
    builder
      .addCase(getStudentByIdentifier.pending, (state) => {
        state.isLoading = true
        state.error = null
        state.student = null
      })
      .addCase(getStudentByIdentifier.fulfilled, (state, action) => {
        state.isLoading = false
        state.student = action.payload
        state.error = null
      })
      .addCase(getStudentByIdentifier.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        state.student = null
      })

    // Validate Students List
    builder
      .addCase(validateStudentsList.pending, (state) => {
        state.isLoading = true
        state.error = null
        state.validatedStudents = null
      })
      .addCase(validateStudentsList.fulfilled, (state, action) => {
        state.isLoading = false
        state.error = null
        state.validatedStudents = action.payload
      })
      .addCase(validateStudentsList.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload?.message || 'Validation failed'
        state.validatedStudents = null
      })
  },
})

export const {setUser, setLoading, setError, clearError, clearValidatedStudents, clearStudent } = usersSlice.actions

// Selectors
export const selectStudent = (state: { users: UsersState }) => state.users.student
export const selectValidatedStudents = (state: { users: UsersState }) => state.users.validatedStudents
export const selectUsersLoading = (state: { users: UsersState }) => state.users.isLoading
export const selectUsersError = (state: { users: UsersState }) => state.users.error

export default usersSlice.reducer
